import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { getCategoryName } from '@/utils/content';
import StatusBadge from '@/components/base/StatusBadge';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
  indexText?: string;
}

export default function ArticleCard({ article, variant = 'default', indexText }: ArticleCardProps) {
  const href = `/article/${article.id}`;

  if (variant === 'horizontal') {
    return (
      <article className="group flex flex-col gap-4 border-b border-background-300 pb-5 sm:flex-row sm:items-stretch">
        <Link
          to={href}
          className="relative block h-44 w-full shrink-0 overflow-hidden rounded-lg bg-background-200 sm:h-32 sm:w-52"
        >
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title}｜爆料反转与辟谣澄清`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800 font-label">
                {getCategoryName(article.category)}
              </span>
              <StatusBadge status={article.status} />
            </div>
            <h3 className="text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
              <Link to={href}>{article.title}</Link>
            </h3>
            <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{article.summary}</p>
          </div>
          <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500 font-label">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
              热度 {article.heat}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group flex items-start gap-3">
        {indexText ? (
          <span className="mt-0.5 w-6 shrink-0 font-heading text-lg font-bold text-background-400 transition-colors group-hover:text-primary-500">
            {indexText}
          </span>
        ) : null}
        <div className="min-w-0">
          <h4 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
            <Link to={href}>{article.title}</Link>
          </h4>
          <p className="mt-1 text-xs text-foreground-500 font-label">
            {getCategoryName(article.category)} · 热度 {article.heat}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-300 bg-background-50 transition-colors duration-300 hover:border-primary-300">
      <Link to={href} className="relative block aspect-[16/10] w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          title={`${article.title}｜黑料反转真相`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3">
          <StatusBadge status={article.status} className="border-transparent bg-background-50/90 backdrop-blur" />
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-2 flex items-center gap-2 text-xs font-label text-foreground-500">
          <span className="font-medium text-secondary-700">{getCategoryName(article.category)}</span>
          <span className="h-3 w-px bg-background-400" />
          <span>{article.publishDate}</span>
        </div>
        <h3 className="text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
          <Link to={href}>{article.title}</Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.summary}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs font-label text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" aria-hidden="true" />
            热度 {article.heat}
          </span>
          <span className="flex items-center gap-1 text-primary-700 transition-all group-hover:gap-2">
            查看走向
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </span>
        </div>
      </div>
    </article>
  );
}